import React, { useContext } from "react";
import { momentLocalizer, type ViewProps } from "react-big-calendar";
// @ts-ignore
import AgendaBase from "react-big-calendar/lib/Agenda";
// @ts-ignore
import Day from "react-big-calendar/lib/Day";
// @ts-ignore
import * as dates from "react-big-calendar/lib/utils/dates";
// @ts-ignore
import { inRange } from "react-big-calendar/lib/utils/eventLevels";

import cx from "classnames";
import fastdom from "fastdom";
import { debounce, isEqual, omit } from "lodash";
import moment, { type Moment } from "moment";
import { commitLocalUpdate, graphql } from "relay-runtime";

import { LoadMoreButton } from "~/common";
import { environment } from "~/models";
import { AgendaContext, type AgendaContextType, type DayEvent } from "~/nav/panels/Meetings";
import { AgendaViewDayEvent } from "~/nav/panels/Meetings/AgendaViewDayEvent";
import { getActiveCalendar } from "~/nav/panels/Meetings/utils";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import styles from "~/nav/panels/Meetings/MeetingsPanel.global.module.css";

const localizer = momentLocalizer(moment);
export const HideEventMutation = graphql`
	mutation Events_HideEventMutation($guid: String!) {
		hideEvent(guid: $guid) {
			ok
		}
	}
`;

export const viewerCalendarsFragment = graphql`
	fragment EventsCalendars on Viewer {
		calendars {
			edges {
				node {
					id
					source
					sourcePrimary
					syncEnabled
					name
					color
					lastSyncDatetime
					hadErrorOnSync
				}
			}
		}
	}
`;

export function invalidateHiddenEvent(id: string) {
	commitLocalUpdate(environment, store => {
		const event = store.get(id);
		if (event) {
			event.invalidateRecord();
		}
	});
}

function DayView(props: ViewProps<DayEvent>) {
	const { events, ...rest } = props;
	const { loading } = useContext(AgendaContext);

	if (!events) {
		return null;
	}

	const updatedEvents = events.map((e: DayEvent) => ({
		...e,
		start: moment(e.start || undefined).toDate(),
		end: moment(e.end || undefined).toDate(),
	}));

	const withChangedEvents = {
		...rest,
		events: updatedEvents,
	};

	return (
		<>
			{loading && <LoadingSpinner />}
			<Day {...withChangedEvents} />
		</>
	);
}

DayView.title = Day.title;
DayView.range = Day.range;
DayView.navigate = Day.navigate;

interface TimeSlotWrapperProps {
	earliestEventTime: Moment;
	latestEventTime: Moment;
}

export const TimeSlotWrapper = ({ earliestEventTime, latestEventTime }: TimeSlotWrapperProps) =>
	function TimeSlot(props: React.PropsWithChildren<{ value: Date }>) {
		const isBeforeEvents = moment(props.value).isBefore(earliestEventTime);
		const isAfterEvents = moment(props.value).isSameOrAfter(latestEventTime);

		return (
			<div className={cx("rbc-timeslot-wrapper", { "rbc-timeslot-eventless": isBeforeEvents || isAfterEvents })}>
				{props.children}
			</div>
		);
	};

type AgendaViewProps = ViewProps<DayEvent> & AgendaContextType;

class AgendaViewWrapped extends React.Component<AgendaViewProps> {
	static defaultProps = { length: 7 };
	agendaContent = React.createRef<HTMLDivElement>();

	onDoubleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		return this.props.onSelectEvent(null);
	};

	private onContentScroll = debounce(
		() => {
			const target = this.agendaContent.current;
			if (!target) {
				return;
			}
			fastdom.measure(() => {
				if (target.scrollHeight - target.clientHeight < 50) {
					return;
				}
				const atBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
				const atTop = target.scrollTop < 50;

				if (atBottom && atTop) {
					return;
				}

				if (atTop && !this.props.prevButtonDisplayed) {
					this.props.onTopScrolled();
				} else if (atBottom && !this.props.nextButtonDisplayed) {
					this.props.onBottomScrolled();
				} else {
					// Check to see the highest visible date
					// This won't be that slow since we max out to 14 days showing
					// We use the string date here to avoid moment.
					let newDate: string = this.props.currentDate.toJSON().slice(0, 10);
					const containerTop = target.getBoundingClientRect().top;
					const possibleDates = target.querySelectorAll<HTMLElement>(":scope div.rbc-agenda-day[data-date]");
					possibleDates.forEach(element => {
						if (element.getBoundingClientRect().top - containerTop < 10) {
							if (element.dataset.date) newDate = element.dataset.date;
						}
					});

					this.props.onDateScrolled(moment(newDate));
				}
			});
		},
		100,
		{ leading: false, trailing: true },
	);

	shouldComponentUpdate(nextProps: AgendaViewProps) {
		return !isEqual(
			omit(nextProps, ["localizer", "getters", "accessors", "components"]),
			omit(this.props, ["localizer", "getters", "accessors", "components"]),
		);
	}

	componentDidMount() {
		const { events } = this.props;
		if (!events || events.length === 0) {
			return;
		}
		this.scrollToDate(false, false, false);
	}
	componentDidUpdate(prevProps: AgendaViewProps) {
		const { prevLoading, nextLoading, loading, events, currentDate, visible } = this.props;
		if (!events || events.length === 0) {
			return;
		}

		const prevLoaded = prevProps.prevLoading && !prevLoading;
		const nextLoaded = prevProps.nextLoading && !nextLoading;
		const loaded = prevProps.loading && !loading;
		const justBecameVisible = !prevProps.visible && visible;

		// On mobile, when the content of the panel renders there is no scroll height, so we need
		// to re-render when the content becomes visible
		const resetScroll = this.props.context?.isMobile && justBecameVisible;

		// We will try to scroll (with animation) if the date has changed, but not from a scroll.
		// We can tell this if the currentDate and the dropdownDate are equal, and we're loading.
		const tryScroll =
			(prevProps.currentDate != currentDate && !prevProps.loading && loading) ||
			(loaded && isEqual(this.props.events, prevProps.events) && !nextLoading && !prevLoading);
		if (prevLoaded || loaded || nextLoaded || resetScroll || tryScroll) {
			this.scrollToDate(prevLoaded, nextLoaded, !justBecameVisible && tryScroll);
		}
	}

	scrollToDate(prevLoaded: boolean, nextLoaded: boolean, smooth: boolean) {
		const { events, currentDate } = this.props;
		if (!events || events.length === 0) {
			return;
		}
		const range = nextLoaded
			? dates
					.range(events[0].start, currentDate, "day")
					.map((date: Date) => {
						const newDate = new Date(date);
						newDate.setHours(0, -newDate.getTimezoneOffset(), 0, 0);
						return newDate;
					})
					.reverse()
			: dates.range(currentDate, events[events.length - 1].start, "day").map((date: Date) => {
					const newDate = new Date(date);
					newDate.setHours(0, -newDate.getTimezoneOffset(), 0, 0);
					return newDate;
				});
		let date = range.find(
			(day: Date) =>
				!!events.find((e: DayEvent) => {
					if (!e.start) {
						return false;
					}
					const newDate = new Date(e.start);
					newDate.setHours(0, -newDate.getTimezoneOffset(), 0, 0);
					return newDate.getTime() == day.getTime();
				}),
		);
		if (!date) {
			if (events[0].start) {
				date = new Date(events[0].start);
			} else {
				date = new Date();
				date.setHours(0, -date.getTimezoneOffset(), 0, 0);
			}
		}
		const elem = document.getElementById(`agenda-day-${date.toISOString().slice(0, 10)}`);
		if (!elem) {
			return;
		}

		const container = this.agendaContent.current;
		if (container) {
			fastdom.measure(() => {
				const bottomHeight = container.scrollHeight - (elem.offsetTop + elem.scrollHeight);
				const topHeight = elem.offsetTop;

				fastdom.mutate(() => {
					if (topHeight < container.clientHeight) {
						this.props.setPrevButtonDisplayed(true);
					}
					if (bottomHeight < container.clientHeight) {
						this.props.setNextButtonDisplayed(true);
					}
				});

				if (!nextLoaded) {
					const scrollOffset = elem.offsetTop - (prevLoaded ? 165 : 0);
					fastdom.mutate(() => {
						container.scroll({ left: 0, top: scrollOffset, behavior: smooth ? "smooth" : "auto" });
					});
				}
			});
		}
	}
	renderNoEvents() {
		const { length, date } = this.props;
		const end = dates.add(date, length, "day");

		const startDate = moment(date);
		const endDate = moment(end);
		const startFormat =
			startDate.year() === moment().year()
				? localizer.format(startDate.toDate(), "MMM DD", "en")
				: localizer.format(startDate.toDate(), "MMM DD, YYYY", "en");
		const endFormat =
			endDate.year() === moment().year()
				? localizer.format(endDate.toDate(), "MMM DD", "en")
				: localizer.format(endDate.toDate(), "MMM DD, YYYY", "en");

		return <div className="rbc-no-events">{`No events found between ${startFormat} and ${endFormat}`}</div>;
	}

	render() {
		const {
			length,
			date,
			loading,
			prevLoading,
			nextLoading,
			prevButtonDisplayed,
			nextButtonDisplayed,
			onTopScrolled,
			onBottomScrolled,
			calendars,
			answerEventAttendance,
		} = this.props;
		let { events } = this.props;
		let month: number | null = null;

		const end = dates.add(date, length, "day");
		const range: Array<Date> = dates.range(date, end, "day");

		const activeCalendar = getActiveCalendar(calendars);

		if (!events || !activeCalendar) {
			return null;
		}

		events = events.filter(event => inRange(event, date, end, this.props.accessors, this.props.localizer));
		events.sort((a: DayEvent, b: DayEvent) => +(a.start ? new Date(a.start) : 0) - +(b.start ? new Date(b.start) : 0));
		const eventMap: { [date: string]: typeof events } = {};
		let dateIndex = 0;
		events.forEach(event => {
			// Precompute the events for each day for below function.
			// This assumes that the events have been pre-sorted above.
			if (!event.start) return;
			const startDate = event.start ? new Date(event.start) : new Date();
			startDate.setHours(0, 0, 0, 0);
			const endDate = event.end ? new Date(event.end) : new Date();
			endDate.setHours(0, 0, 0, 0);
			if (event.isAllDay) endDate.setTime(endDate.getTime() - 1); // subtract 1ms if all-day

			while (startDate > range[dateIndex]) {
				dateIndex++;
			}
			if (eventMap[range[dateIndex].toString()] === undefined) {
				eventMap[range[dateIndex].toString()] = [];
			}

			eventMap[range[dateIndex].toString()]?.push(event);
			let endIndex = dateIndex + 1;

			while (endIndex < range.length && endDate >= range[endIndex]) {
				if (eventMap[range[endIndex].toString()] === undefined) {
					eventMap[range[endIndex].toString()] = [];
				}
				eventMap[range[endIndex].toString()]?.push(event);
				endIndex++;
			}
		});

		const noEvents = events.length === 0;
		const today = moment().startOf("day").toDate();

		return (
			<div className="rbc-agenda-view" ref={this.agendaContent} onScroll={this.onContentScroll}>
				{noEvents && !loading && this.renderNoEvents()}

				{noEvents && loading ? (
					<LoadingSpinner />
				) : (
					<div className={cx("rbc-agenda-content", { "rbc-loading": loading })}>
						{prevLoading && (
							<div className={cx(styles.pageLoader, styles.pageLoader__top)}>
								<LoadingSpinner size="xs" />
							</div>
						)}

						{prevButtonDisplayed && (
							<div className={styles.loadMoreWrapper__top}>
								<LoadMoreButton
									loadMore={onTopScrolled}
									loadingUp
									fireOnMount={false}
									autoLoad={false}
									dotted={false}
								/>
							</div>
						)}

						{range.map((day: Date) => {
							const dayEvents = eventMap[day.toString()] || [];
							const newMonth = month !== day.getMonth();

							if (!dayEvents.length) return null;

							month = day.getMonth();
							const format = day.getFullYear() === moment().year() ? "MMM" : "MMM YYYY";

							return (
								<div
									key={day.toString()}
									className="rbc-agenda-day"
									onDoubleClick={this.onDoubleClick}
									data-date={moment(day).format("YYYY-MM-DD")}
									id={`agenda-day-${moment(day).format("YYYY-MM-DD")}`}
								>
									{newMonth && (
										<div
											key={month}
											className={cx(
												"rbc-agenda-month-banner",
												`rbc-agenda-month-banner--bubblePattern${(month % 3) + 1}`,
											)}
										>
											{localizer.format(day, format, "en")}
										</div>
									)}

									<div className={cx("rbc-agenda-day-label", { "rbc-today": day === date })}>
										<div
											className={cx("rbc-agenda-day-label-num", {
												[styles.todayLabel]: day.getTime() === today.getTime(),
											})}
										>
											{localizer.format(day, "DD", "en")}
										</div>
										<div className="rbc-agenda-day-label-name">{localizer.format(day, "dddd", "en")}</div>
									</div>

									{dayEvents.map(event => {
										return (
											<AgendaViewDayEvent
												key={`${day.toString()}_${event.id}`}
												{...omit(this.props, ["ref"])}
												day={day}
												event={event}
												activeCalendar={activeCalendar}
												answerEventAttendance={answerEventAttendance}
												uxSimplificationEnabled={this.props.uxSimplificationEnabled}
											/>
										);
									})}
								</div>
							);
						})}

						{nextLoading && (
							<div className={cx(styles.pageLoader, styles.pageLoader__bottom)}>
								<LoadingSpinner size="xs" />
							</div>
						)}

						{nextButtonDisplayed && (
							<div className={styles.loadMoreWrapper__bottom}>
								<LoadMoreButton loadMore={onBottomScrolled} fireOnMount={false} autoLoad={false} dotted={false} />
							</div>
						)}
					</div>
				)}
			</div>
		);
	}
}

function AgendaView(props: ViewProps<DayEvent>) {
	return (
		<AgendaContext.Consumer>
			{contextProps => {
				return <AgendaViewWrapped {...omit(props, ["ref"])} {...contextProps} />;
			}}
		</AgendaContext.Consumer>
	);
}

AgendaView.title = AgendaBase.title;
AgendaView.range = AgendaBase.range;
AgendaView.navigate = AgendaBase.navigate;

export { AgendaView, DayView };
